/* eslint-disable eqeqeq */
import { createElement, useEffect, Suspense } from "react"

import { Switch, Route, useHistory, useLocation } from "react-router-dom"

import { ErrorBoundary, ErrorMessage } from "web/components"

import { useBaseURL } from "web/hooks/context"

export default function Navigator({
  routes = [],
  subRoutes,
  requireSubRoute = false,
  component,
  childProps,
  preserveSubPath = false,
  render,
}) {
  const history = useHistory()
  const { pathname } = useLocation()
  const baseURL = useBaseURL()

  const hasRoutes = routes.length !== 0
  const hasSubRoutes = subRoutes !== undefined

  const routeFromPath = pathname.match(`${baseURL}(.*)`)[1].split("/").slice(1)

  const pathMatchesValidRoute =
    hasRoutes &&
    routes.find((route) => route.key == routeFromPath[0]) !== undefined

  const pathMatchesValidSubRoute =
    !hasSubRoutes ||
    (pathMatchesValidRoute &&
      (subRoutes[routeFromPath[0]].length === 0 ||
        subRoutes[routeFromPath[0]].find(
          (subRoute) => subRoute.key == routeFromPath[1]
        ) !== undefined))

  const routeFromPathValid =
    pathMatchesValidRoute &&
    ((!requireSubRoute && !routeFromPath[1]) || pathMatchesValidSubRoute)

  const selectedRouteKey = routeFromPathValid
    ? routeFromPath
    : hasRoutes
      ? [
          routes[0].key,
          requireSubRoute && hasSubRoutes
            ? (subRoutes[routes[0].key][0] || {}).key || null
            : null,
        ]
      : [null, null]

  function getURLForRoute(_selectedRouteKey) {
    return `${baseURL}/${_selectedRouteKey[0] || ""}${
      hasSubRoutes ? `/${_selectedRouteKey[1] || ""}` : ""
    }`
  }

  useEffect(() => {
    if (!routeFromPathValid) {
      history.replace(getURLForRoute(selectedRouteKey))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeFromPathValid])

  function getRouterRoutes() {
    const routerRoutes = routes.map((route) => (
      <Route
        key={route.key}
        exact={route.key === "" || hasSubRoutes}
        path={`${baseURL}/${route.key}`}
      >
        <ErrorBoundary
          fallback={
            <ErrorMessage message="Could not load page. Try again. Contact support if the problem persists." />
          }
        >
          <Suspense fallback={null}>
            {createElement(route.component || component, {
              ...getRouteProps([route]),
            })}
          </Suspense>
        </ErrorBoundary>
      </Route>
    ))

    if (hasSubRoutes) {
      for (const route of routes) {
        if (!(route.key in subRoutes)) continue
        routerRoutes.push(
          ...subRoutes[route.key].map((subRoute) => (
            <Route
              key={`${route.key}-${subRoute.key}`}
              exact={route.key === ""}
              path={`${baseURL}/${route.key}/${subRoute.key}`}
            >
              {createElement(route.component || component, {
                ...getRouteProps([route, subRoute]),
              })}
            </Route>
          ))
        )
      }
    }

    return routerRoutes
  }

  function getRouteProps([route, subRoute]) {
    const hasSubRoute = subRoute !== undefined

    function resolveProp(prop) {
      if (typeof prop === "function") {
        return prop(hasSubRoute ? [route, subRoute] : route)
      } else {
        return prop
      }
    }

    const defaultProps = { route: [route, subRoute] }
    const props = hasSubRoute ? subRoute.props : route.props

    if (!Array.isArray(props)) return defaultProps

    return props.reduce(
      (props, prop) => ({ ...props, [prop]: resolveProp(childProps[prop]) }),
      defaultProps
    )
  }

  const handleSelectRoute = (routeKey) => {
    const newURL = getURLForRoute(routeKey)

    if (selectedRouteKey === "" || !preserveSubPath) {
      window.Turbo.visit(newURL)
      return
    }

    const prevURL = getURLForRoute(selectedRouteKey)
    window.Turbo.visit(pathname.replace(prevURL, newURL))
  }

  return render({
    routes,
    subRoutes,
    selectedRouteKey,
    onSelectRoute: handleSelectRoute,
    destinations: <Switch>{getRouterRoutes()}</Switch>,
  })
}
